import { Component, HostListener, OnInit } from '@angular/core';
import { NgForOf, NgIf, TitleCasePipe } from '@angular/common';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ApiService } from '../../shared/services/api.service';
import moment from 'moment';
import { catchError, finalize, forkJoin, Observable, of } from 'rxjs';
import { StatsBreakdownItem, StatsSummaryItem } from '../../models/stats';
import { ToastrService } from 'ngx-toastr';
import { AgencyWithAccounts } from '../../models/agency.model';
import { Chart, ChartConfiguration, Plugin } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { ActivatedRoute, Router } from '@angular/router';

type GroupBy = 'provider' | 'agent' | 'application' | 'agency';
type RangeBy = 'day' | 'week' | 'month' | 'year';

const GROUP_BY: readonly GroupBy[] = ['provider', 'agent', 'application', 'agency'];

const RANGE_BY: readonly RangeBy[] = ['day', 'week', 'month', 'year'];

@Component({
  selector: 'app-reports',
  imports: [
    NgIf,
    NgxSpinnerModule,
    TitleCasePipe,
    NgForOf,
    BaseChartDirective,
    NgxDaterangepickerMd,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.scss',
})
export class ReportsComponent implements OnInit {
  loaded = false;
  selectedGroup: GroupBy = 'provider';
  selectedRange: RangeBy = 'month';
  pendingAgencyId: string | null = null;
  selectedAgency: AgencyWithAccounts | null = null;
  agencies: AgencyWithAccounts[] = [];
  statsSummaryDataPerApp: StatsSummaryItem[][] = [];
  statsSummaryData: StatsSummaryItem[] = [];
  totalStatsSummaryPerApp: Record<string, number>[] = [];
  statsBreakdownData: StatsBreakdownItem[] = [];
  isExportDropdownOpen = false;

  ranges: any = {
    Today: [moment(), moment()],
    Yesterday: [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
    'Last 7 Days': [moment().subtract(6, 'days'), moment()],
    'Last 30 Days': [moment().subtract(29, 'days'), moment()],
    'This Month': [moment().startOf('month'), moment().endOf('month')],
    'Last Month': [
      moment().subtract(1, 'month').startOf('month'),
      moment().subtract(1, 'month').endOf('month'),
    ],
    'This Year': [moment().startOf('year'), moment().endOf('year')],
    'Last Year': [moment().subtract(1, 'year').startOf('year'), moment().startOf('year')],
  };
  selectedDateRange = {
    startDate: moment().subtract(29, 'days'),
    endDate: moment(),
  };

  doughnutChartLabels: string[] = [];
  doughnutChartData = {
    labels: this.doughnutChartLabels,
    datasets: [
      {
        data: [0, 0, 0, 0, 0],
        backgroundColor: ['#5CC8FF', '#7DDBC1', '#F3B340', '#F9745F', '#A3A3A3'],
      },
    ],
  };
  doughnutChartOptions: ChartConfiguration<'doughnut'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
  };
  allowedOrderTypes = ['Booked', 'Issued', 'Voided', 'Expired', 'Cancelled'];
  chartLabelKeyMap: { [label: string]: keyof StatsSummaryItem } = {
    Booked: 'orders_created',
    Issued: 'orders_issued',
    Expired: 'orders_expired',
    Voided: 'orders_voided',
    Canceled: 'orders_cancelled',
  };

  lineChartData: ChartConfiguration<'line'>['data'] = {
    labels: [],
    datasets: [],
  };

  lineChartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    elements: {
      line: { tension: 0.3 },
      point: {
        radius: 3,
        hoverRadius: 5,
        borderWidth: 1,
      },
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          boxWidth: 20,
          boxHeight: 10,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        usePointStyle: true,
        callbacks: {
          labelPointStyle: () => ({
            pointStyle: 'circle',
            rotation: 0,
          }),
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: '#f0f0f0',
        },
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  };

  emailForm!: FormGroup;
  recipientEmails: string[] = [];
  selectedRecipientEmails: string[] = [];

  verticalDashedLinePlugin: Plugin = {
    id: 'verticalDashedLinePlugin',
    afterDraw: (chart: Chart) => {
      const tooltip = chart.tooltip as any;
      const activeElements = tooltip?.getActiveElements();

      if (activeElements && activeElements.length > 0) {
        const activePoint = activeElements[0];
        if (!activePoint) return;

        const datasetMeta = chart.getDatasetMeta(activePoint.datasetIndex);
        if (!datasetMeta) return;

        const pointElement = datasetMeta.data[activePoint.index];
        if (!pointElement) return;

        const ctx = chart.ctx;
        const x = pointElement.x;
        const topY = chart.chartArea.top;
        const bottomY = chart.chartArea.bottom;

        ctx.save();
        ctx.beginPath();
        ctx.setLineDash([3, 3]);
        ctx.moveTo(x, topY);
        ctx.lineTo(x, bottomY);
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.4)';
        ctx.stroke();
        ctx.restore();
      }
    },
  };

  constructor(
    private apiService: ApiService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private fb: FormBuilder,
    private ls: LocalStorageService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.dropdown')) {
      this.isExportDropdownOpen = false;
    }
  }

  ngOnInit() {
    this.buildEmailForm();
    this.parseUrl();
    this.loadStatsWithMappings();
  }

  private fetchStatsSummary(): Observable<StatsSummaryItem[]> {
    const dateStart = this.selectedDateRange?.startDate?.format('YYYY-MM-DD');
    const dateEnd = this.selectedDateRange?.endDate?.format('YYYY-MM-DD');
    const listing = this.selectedGroup || 'provider';
    const agencyId = this.selectedAgency?.id || this.pendingAgencyId;

    return this.apiService.getStatsSummary({
      listing,
      date_start: dateStart,
      date_end: dateEnd,
      agency_id: agencyId,
    });
  }

  private fetchStatsBreakdown(): Observable<StatsBreakdownItem[]> {
    const dateStart = this.selectedDateRange?.startDate?.format('YYYY-MM-DD');
    const dateEnd = this.selectedDateRange?.endDate?.format('YYYY-MM-DD');
    const range = this.selectedRange;
    const agencyId = this.selectedAgency?.id || this.pendingAgencyId;

    return this.apiService.getStatsBreakdown({
      range,
      date_start: dateStart,
      date_end: dateEnd,
      agency_id: agencyId,
    });
  }

  loadStatsWithMappings(): void {
    this.loaded = false;
    this.spinner.show();

    const subagencyStatsAllowed = this.ls.getBool('isSuperagency');

    const requests: {
      statsSummary: Observable<StatsSummaryItem[]>;
      statsBreakdown: Observable<StatsBreakdownItem[]>;
      agencies?: Observable<AgencyWithAccounts[]>;
    } = {
      statsSummary: this.fetchStatsSummary().pipe(
        catchError(error => {
          console.error('Failed to fetch stats summary:', error);
          return of([]);
        })
      ),
      statsBreakdown: this.fetchStatsBreakdown().pipe(
        catchError(error => {
          console.error('Failed to fetch stats breakdown:', error);
          return of([]);
        })
      ),
    };

    if (subagencyStatsAllowed) {
      requests.agencies = this.apiService
        .getAgenciesWithAccounts({ include_accounts: false, include_superagency: true })
        .pipe(
          catchError(error => {
            console.error('Failed to fetch agencies:', error);
            return of([]);
          })
        );
    }

    forkJoin(requests)
      .pipe(
        finalize(() => {
          this.loaded = true;
          this.spinner.hide();
        })
      )
      .subscribe(({ statsSummary, statsBreakdown, agencies }) => {
        if (subagencyStatsAllowed && agencies) {
          this.agencies = agencies;

          if (this.pendingAgencyId) {
            this.selectedAgency = this.agencies?.find(a => a.id === this.pendingAgencyId) ?? null;

            this.pendingAgencyId = null;

            if (!this.selectedAgency) {
              // Remove invalid agencyId from URL
              this.router.navigate([], {
                relativeTo: this.route,
                queryParams: { agencyId: null },
                queryParamsHandling: 'merge',
              });

              this.loadStatsWithMappings();
              return;
            }
          }
        }

        this.statsSummaryData = statsSummary;
        this.statsSummaryDataPerApp = this.groupStatsByApplication(this.statsSummaryData);
        this.totalStatsSummaryPerApp = this.calculateTotalStatsSummaryPerAppPerApp(
          this.statsSummaryDataPerApp
        );
        this.generateDoughnutChartData(statsSummary);

        this.statsBreakdownData = statsBreakdown;
        this.generateLineChartData(statsBreakdown);
      });
  }

  loadStatsSummaryOnly(): void {
    this.loaded = false;
    this.spinner.show();

    this.fetchStatsSummary()
      .pipe(
        finalize(() => {
          this.loaded = true;
          this.spinner.hide();
        })
      )
      .subscribe({
        next: statsSummary => {
          this.statsSummaryData = statsSummary;
          this.statsSummaryDataPerApp = this.groupStatsByApplication(this.statsSummaryData);
          this.totalStatsSummaryPerApp = this.calculateTotalStatsSummaryPerAppPerApp(
            this.statsSummaryDataPerApp
          );
          this.generateDoughnutChartData(statsSummary);
        },
        error: error => {
          console.error('Failed to fetch stats summary:', error);
        },
      });
  }

  loadStatsBreakdownOnly(): void {
    this.loaded = false;
    this.spinner.show();

    this.fetchStatsBreakdown()
      .pipe(
        finalize(() => {
          this.loaded = true;
          this.spinner.hide();
        })
      )
      .subscribe({
        next: statsBreakdown => {
          this.statsBreakdownData = statsBreakdown;
          this.generateLineChartData(statsBreakdown);
        },
        error: error => {
          console.error('Failed to fetch stats breakdown:', error);
        },
      });
  }

  generateDoughnutChartData(statsSummary: StatsSummaryItem[]) {
    if (statsSummary) {
      const labels = Object.keys(this.chartLabelKeyMap);
      const data = labels.map(label =>
        statsSummary.reduce((sum, item) => sum + Number(item[this.chartLabelKeyMap[label]] ?? 0), 0)
      );

      this.doughnutChartLabels = labels;
      this.doughnutChartData = {
        labels: this.doughnutChartLabels,
        datasets: [
          {
            data,
            backgroundColor: ['#5CC8FF', '#7DDBC1', '#F3B340', '#F9745F', '#A3A3A3'],
          },
        ],
      };
    } else {
      this.doughnutChartData = {
        labels: [],
        datasets: [],
      };
    }
  }

  generateLineChartData(statsBreakdown: StatsBreakdownItem[]) {
    if (statsBreakdown) {
      const labelMap = new Map<string, string>();
      const typeMap = new Map<string, Map<string, number>>();

      for (const stat of statsBreakdown) {
        let labelKey: string;
        let labelDisplay: string;

        switch (this.selectedRange) {
          case 'day':
            labelKey = stat.created_at!;
            labelDisplay = stat.created_at!;
            break;
          case 'month':
            labelKey = stat.created_at!;
            labelDisplay = this.formatMonthWithYear(labelKey);
            break;
          case 'year':
            labelKey = stat.created_at!;
            labelDisplay = stat.created_at!;
            break;
          case 'week':
            labelKey = `${stat.year}-W${stat.week?.padStart(2, '0')}`;
            labelDisplay = `${stat.year} W${stat.week?.padStart(2, '0')}`;
            break;
          default:
            continue;
        }

        labelMap.set(labelKey, labelDisplay);

        const typeLabel = this.mapTypeLabel(stat.type);
        if (!this.allowedOrderTypes.includes(typeLabel)) continue;

        if (!typeMap.has(typeLabel)) {
          typeMap.set(typeLabel, new Map());
        }

        const typeData = typeMap.get(typeLabel)!;
        typeData.set(labelKey, (typeData.get(labelKey) || 0) + stat.count);
      }

      // Sort label keys
      const sortedKeys = Array.from(labelMap.keys()).sort((a, b) => a.localeCompare(b));

      // Use the sorted formatted labels
      const sortedLabels = sortedKeys.map(key => labelMap.get(key)!);

      const datasets = this.allowedOrderTypes.map(type => {
        const values = typeMap.get(type) ?? new Map();
        return {
          label: type,
          data: sortedKeys.map(key => values.get(key) ?? 0),
          borderColor: this.getColorForType(type),
          backgroundColor: '#fff',
          pointRadius: 3,
          pointHoverRadius: 5,
          borderWidth: 2,
          fill: false,
          tension: 0.3,
        };
      });

      if (!sortedKeys.length || !datasets.length) {
        return;
      }

      this.lineChartData.labels = sortedLabels;
      this.lineChartData.datasets = datasets;
    } else {
      this.lineChartData = {
        labels: [],
        datasets: [],
      };
    }
  }

  formatMonthWithYear(dateStr: string): string {
    const [year, month] = dateStr.split('-');
    const shortMonth = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ][+month - 1];
    return `${shortMonth} ${year.slice(2)}`;
  }

  mapTypeLabel(type: string): string {
    switch (type) {
      case 'created':
        return 'Booked';
      case 'OrderStarted':
        return 'Started';
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  }

  getColorForType(type: string): string {
    const map: Record<string, string> = {
      Booked: '#5CC8FF',
      Issued: '#7DDBC1',
      Voided: '#F3B340',
      Cancelled: '#A3A3A3',
      Expired: '#C0C0C0',
      Started: '#E63757',
    };
    return map[type] || '#888';
  }

  onDateRangeChange(event: any) {
    if (event?.startDate && event?.endDate) {
      this.selectedDateRange = {
        startDate: event.startDate,
        endDate: event.endDate,
      };

      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          from: event.startDate.format('YYYY-MM-DD'),
          to: event.endDate.format('YYYY-MM-DD'),
        },
        queryParamsHandling: 'merge',
      });

      this.loadStatsSummaryOnly();
      this.loadStatsBreakdownOnly();
    }
  }

  selectRange(value: RangeBy) {
    this.selectedRange = value;
    this.loadStatsBreakdownOnly();

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { range: this.selectedRange },
      queryParamsHandling: 'merge',
    });
  }

  selectGroup(value: GroupBy) {
    this.selectedGroup = value;
    this.loadStatsSummaryOnly();

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { group: this.selectedGroup },
      queryParamsHandling: 'merge',
    });
  }

  selectAgency(agency: AgencyWithAccounts | null) {
    this.selectedAgency = agency;
    this.loadStatsSummaryOnly();
    this.loadStatsBreakdownOnly();

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        agencyId: agency?.id ?? null,
      },
      queryParamsHandling: 'merge',
    });
  }

  buildEmailForm() {
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
    this.setPassengerEmails();
  }

  setPassengerEmails() {
    this.recipientEmails = [];
    this.selectedRecipientEmails = [];
    const email = this.ls.getItem('email');
    if (email) {
      this.recipientEmails.push(email);
    }
  }

  addEmail() {
    const email = this.emailForm.get('email')?.value;
    if (!email || this.recipientEmails.includes(email)) {
      return;
    }
    this.recipientEmails.push(email);
    this.selectedRecipientEmails.push(email);
    this.emailForm.reset();
  }

  onPassengerEmailsChange(email: string) {
    if (!this.selectedRecipientEmails.includes(email)) {
      this.selectedRecipientEmails.push(email);
    } else {
      this.selectedRecipientEmails.splice(this.selectedRecipientEmails.indexOf(email), 1);
    }
  }

  export() {
    this.apiService
      .orderExport({
        issuingDateFrom: this.selectedDateRange?.startDate?.format('YYYY-MM-DD'),
        issuingDateTo: this.selectedDateRange?.endDate?.format('YYYY-MM-DD'),
        recipientEmails: this.selectedRecipientEmails,
      })
      .subscribe({
        next: response => {
          if (response.ok) {
            this.isExportDropdownOpen = false;
            this.toastr.success('Export successful', 'Success');
          }
        },
        error: err => {
          console.error('Export failed:', err);
        },
      });
  }

  groupStatsByApplication(stats: StatsSummaryItem[]): StatsSummaryItem[][] {
    const groupedStats = stats.reduce(
      (acc, item) => {
        const appId = item.application?.id ?? 'unknown';
        if (!acc[appId]) acc[appId] = [];
        acc[appId].push(item);
        return acc;
      },
      {} as Record<string, StatsSummaryItem[]>
    );

    return Object.values(groupedStats)?.map(group =>
      group?.sort((a, b) => b.orders_created - a.orders_created)
    );
  }

  calculateTotalStatsSummaryPerAppPerApp(stats: StatsSummaryItem[][]): Record<string, number>[] {
    if (!stats.length) return [];

    return stats.map(group => {
      const totals: Record<string, number> = {};
      const firstItem = group[0];
      if (!firstItem) return totals;

      Object.keys(firstItem).forEach(key => {
        if (key.startsWith('orders')) {
          totals[key] = this.getSumByKey(group, key);
        }
      });

      return totals;
    });
  }

  getSumByKey(arr: any[], key: string): number {
    return arr.reduce((accumulator, current) => {
      const value = Number(current?.[key]) || 0;
      return accumulator + value;
    }, 0);
  }

  parseUrl(): void {
    let params = { ...this.route.snapshot.queryParams };

    // Dates
    const from = params['from'];
    const to = params['to'];

    if (from && to) {
      this.selectedDateRange = {
        startDate: moment(from),
        endDate: moment(to),
      };
    }

    // Range
    const range = params['range'];
    this.selectedRange = RANGE_BY.includes(range) ? range : 'month';

    // Group
    const group = params['group'];
    this.selectedGroup = GROUP_BY.includes(group) ? group : 'provider';

    // Agency
    this.pendingAgencyId = params['agencyId'] ?? null;
  }
}
